import {
    computed,
    effect,
    signal, untracked
} from '@angular/core';
import type {
    AgPromise,
    ColDef,
    ColGroupDef,
    IHeaderComp,
    IHeaderParams,
    IInnerHeaderComponent
} from 'ag-grid-community';
import { DataType } from 'src/app/pbir/dataModel/DataType';
import { SortDirection } from 'src/app/services/api.services';
import { VisualComponent } from '../../base/Visual.component';
import { VisualDataAdapter } from '../../data/VisualDataAdapter';
import { VisualField } from '../../VisualField';
import * as AgGridConstants from '../shared';
import { AgGridPivotTableVisualComponent } from './PivotTableVisual.component';
import { dataComparer } from './dataComparer';

/**
 * Manages array of values that can be referenced with unique keys.
 * Order of inserted values is preserved.
 **/
export class KeyedList<K, T> extends Array<T> {
  private _map = new Map<K, number>();

  constructor() { 
    super();
  }
  
  keyFindIndex(key: K): number | undefined {
    return this._map.get(key);
  }

  keyFind(key: K): T | undefined {
    let index = this.keyFindIndex(key);
    if (index === undefined)
      return undefined;

    return this[index];
  }

  getOrAdd(key: K, factory: (key: K) => T): T {
    let index = this.keyFindIndex(key);
    if (index === undefined) {
      index = this.length;
      this.push(factory(key));
      this._map.set(key, index);
    }

    return this[index];
  }

  set(key: K, value: T): number {
    let index = this.keyFindIndex(key);
    if (index === undefined) {
      index = this.length;
      this.push(value);
      this._map.set(key, index);
    }
    else {
      this[index] = value;
    }

    return index;
  }

  clear() {
    this._map.clear();
    this.length = 0;
  }

  sort(compareFn?: (a: T, b: T) => number) {
    const indexed = [...this._map.entries()].map(([key, index]) => ({
      key: key,
      item: this[index],
    }));

    indexed.sort((a, b) => compareFn(a.item, b.item));

    this.clear();
    indexed.forEach((x, newIndex) => {
      this[newIndex] = x.item;
      this._map.set(x.key, newIndex);
    });

    return this;
  }
}

/** 
 * Provides visual field, formatted data, display string, and raw value.
 **/
export class PivotValue {
  static readonly empty = new PivotValue(null, DataType.Text, '', '', null);

  constructor(
    readonly raw: any,
    readonly dataType: DataType,
    readonly data: any,
    readonly display: string,
    readonly group: PivotGroup) { }
}


export enum PivotGroupType {
  Row,
  Column,
  Value
}

export abstract class PivotGroup {
  constructor(
    readonly pivot: Pivot,
    readonly type: PivotGroupType,
    readonly depth: number,
    readonly vf: VisualField) { }

  dataField: string;

  readonly id = `${PivotGroupType[this.type]}Group_${(this.vf?.target.referenceString() ?? 'null')}`;
 
  get isRow() { return this.type == PivotGroupType.Row; }
  get isColumn() { return this.type == PivotGroupType.Column; }
  get isValue() { return this.type == PivotGroupType.Value; }

  get isSortableColumn() { return this.isRow || this.isValue }

  readonly initialColumnDef = this.computeInitialColumnDef();

  protected computeInitialColumnDef(): ColDef {
    let cellClass = ['ag-grid-data-cell'];

    // IN-6562 Everything is right aligned 🙂
    // if (this.vf?.dataType.isNumeric)
    cellClass.push('ag-grid-cell-right-align');
      
    let shortTitle = this.vf?.shortTitle ?? '';

    let columnDef = <ColDef>{
      ...AgGridConstants.data.ag.columnDef,

      colId:   this.id,
      context: this,
      
      cellClass:     cellClass,
      headerClass:   cellClass,
      headerValueGetter: () => this.vf?.shortTitle ?? '',
      
      headerComponentParams: {
        innerHeaderComponentParams: {
          injector: this.pivot.component.injector,
          vf:       this.vf,
        },

        innerHeaderComponent: class implements IInnerHeaderComponent {
          span: HTMLSpanElement;

          init(params: IHeaderParams<any, any>): AgPromise<void> | void {
            let { injector, vf } = params.innerHeaderComponentParams;

            let span = document.createElement('span');
            span.classList.add('text');

            let shortTitle = vf?.shortTitle ?? '';
            span.textContent = shortTitle;
            span.title       = shortTitle;

            effect(() => {
              let shortTitle = vf?.shortTitle ?? '';
              span.textContent = shortTitle;
              span.title       = shortTitle;
            }, { injector });

            this.span = span;
          }

          getGui(): HTMLElement {
            return this.span;
          }

          refresh(params: IHeaderParams): boolean {
            return true;
          }
        }
      },

      valueGetter:
        this.vf
          ? this.pivot.valueGetter
          : params => '',

      sortable:        true,
      suppressMovable: true,
    };

    return columnDef;
  }


  readonly stateColumnDef = computed<ColDef>(() => this.computeStateColumnDef());

  protected computeStateColumnDef(): ColDef {
    let columnDef: ColDef = {};
    
    if (this.isSortableColumn) {
      let orderBy = untracked(() => this.orderBy());

      columnDef = {
        ...columnDef,
        sort:    
            orderBy?.sortDirection == SortDirection.Ascending  ? 'asc'
          : orderBy?.sortDirection == SortDirection.Descending ? 'desc'
          : <any>false,

        sortIndex: orderBy?.sortOrder,
      };
    }

    if(!!this.vf)
    columnDef = {
      ...columnDef,
      comparator: dataComparer.agGridComparer(this.vf),
    }

    return columnDef;
  }

  readonly columnDef = computed<ColDef>(() => ({
    ...this.initialColumnDef,
    ...this.stateColumnDef()
  }));

  readonly orderBy = computed<VisualDataAdapter.OrderBy>(() => {
    if (!this.vf || !this.isValue)
      return null;

    let [sorted, sortOrder, sortDirection] = [
      this.vf.sorted(), 
      this.vf.sortOrder(),
      this.vf.sortDirection()
    ];

    if (!sorted || !sortDirection)
      return null;

    return {
      target: this.vf.target,
      sortOrder,
      sortDirection,
    };
  });
}

export class PivotRowGroup extends PivotGroup {
  constructor(pivot: Pivot, depth: number, vf: VisualField) { 
    super(pivot, PivotGroupType.Row, depth, vf);
  }

  protected override computeInitialColumnDef(): ColDef {
    let columnDef = super.computeInitialColumnDef();
    
    let defaultWidth = this.vf ? AgGridConstants.getDefaultColumnWidth(this.vf) : undefined;

    columnDef = {
      ...columnDef,
      initialWidth: defaultWidth,

      rowGroup: true,
      hide:     true,
    };

    return columnDef;
  }
}

export class PivotColumnOrValueGroup extends PivotGroup {
  constructor(pivot: Pivot, type: PivotGroupType, depth: number, vf: VisualField) {
    super(pivot, type, depth, vf);
  }
}

export class PivotColumnGroup extends PivotColumnOrValueGroup {
  constructor(pivot, depth: number, vf: VisualField) { 
    super(pivot, PivotGroupType.Column, depth, vf);
  }

  protected override computeInitialColumnDef(): ColDef {
    let columnDef = super.computeInitialColumnDef();

    columnDef = {
      ...columnDef,
      pivot: true,
    };

    return columnDef;
  }

  protected override computeStateColumnDef(): ColDef {
    let columnDef = super.computeStateColumnDef();
    
    return columnDef;
  }
}

export class PivotValueGroup extends PivotColumnOrValueGroup {
  constructor(pivot: Pivot, depth: number, vf: VisualField) { 
    super(pivot, PivotGroupType.Value, depth, vf);
  }

  parentColumn: PivotColumn;

  protected override computeInitialColumnDef(): ColDef {
    let columnDef = super.computeInitialColumnDef();

    let defaultWidth = this.vf ? AgGridConstants.getDefaultColumnWidth(this.vf) : undefined;

    columnDef = {
      ...columnDef,
      initialWidth: defaultWidth,

      aggFunc: 'sum',

      valueGetter: params => {
        let row = params.data as PivotRow;
        if (row instanceof PivotRow) {
          let column = params.colDef.context.parentColumn as PivotColumn;
          let value = row.getValue(this, column);
          return AgGridConstants.AgGridCellData.create(this.pivot.component, value?.data, this.vf, value);
        }

        return '';
      },
      comparator: this.vf ? dataComparer.agGridComparer(this.vf) : null,
    };

    if (!this.vf) {
      columnDef.headerClass = [...<any>columnDef.headerClass, 'ag-pivot-value-group-empty'];
    }
    
    return columnDef;
  }
}


export interface PivotNodeAddResult<N> {
  readonly node: N;
  readonly added: boolean;
  readonly parent?: PivotNodeAddResult<N>;
}

export abstract class PivotNode<TChildKey, TChild extends PivotNode<TChildKey, TChild>> {
  constructor(
    readonly pivot: Pivot,
    readonly ownValue: PivotValue) { }

  readonly id = this.pivot.nextNodeId();

  get shortTitle() { return this.ownValue.display; }
  get longTitle() { return this.ownValue.display; }


  private readonly _children = new KeyedList<TChildKey, KeyedList<PivotValue, TChild>>();

  private getOrAddChild(key: TChildKey, value: PivotValue): PivotNodeAddResult<TChild> {
    let added = false;

    let childList = this._children.getOrAdd(key, key => new KeyedList());

    let child = childList.keyFind(value);
    if (!child) {
      added = true;

      child = this.createChild(key, value);
      childList.set(value, child);
    }

    return {
      node: child,
      added,
      parent: null,
    };
  }

  sortChildren() {
    for (let key of this._children.keys()) {
      let list = this._children.at(key);

      let probeValue = list[0]?.ownValue
      if (!probeValue)
        return;

      let vf = probeValue.group.vf;
      let comparer = dataComparer.getComparerForField(vf);
      let invert = (vf?.sortDirection() ?? SortDirection.Ascending) == SortDirection.Ascending ? 1 : -1;

      list.sort(((a, b) => comparer(a.ownValue.raw, b.ownValue.raw) * invert));

      for (let child of list) {
        child.sortChildren();
      }
    }
  }



  protected abstract createChild(k: TChildKey, value: PivotValue): TChild;
  
  *children(): Iterable<TChild> {
    for (let list of this._children) {
      for (let c of list)
        yield c;
    }
  }


  addChild(key: TChildKey, value: PivotValue): PivotNodeAddResult<TChild> {
    return this.getOrAddChild(key, value);
  }

  addDescendants(
    keyPath: ReadonlyArray<TChildKey>,
    values: ReadonlyArray<PivotValue>): PivotNodeAddResult<TChild> {
    let result = this.getOrAddChild(keyPath[0], values[0]);

    for (let i = 1; i < keyPath.length; ++i) {
      let childResult = result.node.getOrAddChild(keyPath[i], values[i]);
      (childResult as any).parent = result;
      result = childResult;
    }

    return result;
  }

  *descendants(): Iterable<TChild> {
    for (let list of this._children) {
      for (let c of list) {
        yield c;

        for (let d of c.descendants())
          yield d;
      }
    }
  }
}

export class PivotColumn extends PivotNode<PivotColumnOrValueGroup, PivotColumn> {
  constructor(
    readonly columnGroup: PivotColumnOrValueGroup,
    value: PivotValue,
    readonly parent: PivotColumn | null,
    pivot: Pivot) { 
    super(pivot, value);
  }

  protected override createChild(k: PivotColumnOrValueGroup, value: PivotValue): PivotColumn {
    return new PivotColumn(k, value, this, this.pivot);
  }

  hierarchy(): PivotColumn[] {
    let columns: PivotColumn[] = [];

    columns.push(this);

    for (let p = this.parent; p.parent; p = p.parent)
      columns.push(p);

    columns.reverse();
    return columns;
  }


  get columnDef() {
    let children = [...this.children()];

    let name =
      this.columnGroup.vf
        ? this.ownValue.display
        : '';

    return <ColGroupDef>{
      groupId: `${this.id}_${this.columnGroup.id}`,

      headerName:        name,
      headerTooltip:     name,
      headerValueGetter: _ => name,
      
      children: children.length
        ? children.map(x => x.columnDef)
        : this.pivot.valueGroups.map(x => ({
          ...x.initialColumnDef,
          context: <PivotValueGroup>{
            ...x.initialColumnDef.context,
            parentColumn: this,
          },
          colId: `${this.id}_${x.id}`,
        }))
    };
  }
}

export class PivotRow extends PivotNode<PivotRowGroup, PivotRow> {
  constructor(
    readonly rowGroup: PivotRowGroup,
    value: PivotValue,
    readonly parent: PivotRow | null,
    pivot: Pivot) { 
    super(pivot, value);
  }

  protected override createChild(k: PivotRowGroup, value: PivotValue) {
    return new PivotRow(k, value, this, this.pivot);
  }

  hierarchy(): PivotRow[] {
    let rows: PivotRow[] = [];

    rows.push(this);

    for (let p = this.parent; p.parent; p = p.parent)
      rows.push(p);

    rows.reverse();
    return rows;
  }


  private readonly _values = new KeyedList<PivotValueGroup, KeyedList<PivotColumn, PivotValue>>();

  getValue(key: PivotValueGroup, column: PivotColumn): PivotValue | undefined {
    return this._values.keyFind(key)?.keyFind(column);
  }
  
  setValue(key: PivotValueGroup, column: PivotColumn, value: PivotValue) {
    let columnValues = this._values.getOrAdd(key, key => new KeyedList());
    columnValues.set(column, value);
  }
}


export class Pivot {
  private _nextNodeId = 1;

  nextNodeId() { return `${this._nextNodeId++}`; }


  private readonly formatter: VisualComponent.Formatter;
  private readonly fieldValueCache = new Map<PivotGroup, KeyedList<any, PivotValue>>;

  getValue(group: PivotGroup, v: any) {
    if (v instanceof PivotValue) {
      if (v.group === group) 
        return v;

      v = v.raw;
    }

    let cache = this.fieldValueCache.get(group);
    if (!cache) {
      cache = new KeyedList<PivotGroup, PivotValue>();
      this.fieldValueCache.set(group, cache);
    }

    return cache.getOrAdd(v, raw => {
      let data = raw;
      let display = '' + raw;

      if (group.vf) {
        data = this.formatter.getValueData(group.vf, raw);
        display = this.formatter.getValueDisplay(group.vf, raw);
      }

      return new PivotValue(raw, group.vf?.dataType ?? DataType.Text, data, display, group);
    });
  }

  readonly valueGetter = (params: any) => {
    let row = params.data as PivotRow;
    if (!(row instanceof PivotRow))
      return '';

    return AgGridConstants.AgGridCellData.create(this.component, row?.ownValue.data, row.ownValue.group.vf, row?.ownValue);
  };


  readonly rowGroups: ReadonlyArray<PivotRowGroup>;
  readonly columnGroups: ReadonlyArray<PivotColumnOrValueGroup>;
  readonly valueGroups: ReadonlyArray<PivotValueGroup>;
  readonly allGroups: ReadonlyArray<PivotGroup>;

  readonly topRowGroup: PivotRowGroup;
  readonly topRow: PivotRow;
  
  readonly topColumnGroup: PivotColumnOrValueGroup;
  readonly topColumn: PivotColumn;

  constructor(readonly component: AgGridPivotTableVisualComponent) { 
    this.formatter = component.formatter();

    let rowFields     = this.component.rowFields();
    let columnFields  = this.component.columnFields();
    let measureFields = this.component.measureFields();
    
    this.rowGroups    = rowFields.map((x, i) => new PivotRowGroup(this, i, x));

    this.columnGroups =
      columnFields.length > 0
      ? columnFields.map((x, i) => new PivotColumnGroup(this, i, x))
      : [new PivotColumnGroup(this, 0, null)];

    this.valueGroups  = measureFields.map((x, i) => new PivotValueGroup(this, this.columnGroups.length, x));

    if (!this.rowGroups.length && !this.valueGroups.length)
       this.valueGroups = [new PivotValueGroup(this, this.columnGroups.length, null)];

    this.allGroups = [...this.rowGroups, ...this.columnGroups, ...this.valueGroups];
    this.allGroups.forEach((g, i) => g.dataField = `_d${i}`);

    this.topRowGroup = this.rowGroups[0];
    this.topRow = new PivotRow(this.topRowGroup, PivotValue.empty, null, this);

    this.topColumnGroup = this.columnGroups[0];
    this.topColumn = new PivotColumn(this.topColumnGroup, PivotValue.empty, null, this);
  }
  
  /**
   * Processes rows loaded by data source.
   * This will populate rows/row columns, then return AgGrid compatible data.
   **/
  processNewRows(
    groups: ProcessNewRowsGroups,
    dataRows: ReadonlyArray<ReadonlyArray<any>>
  ): ProcessNewRowsResult {
    let rowData: PivotRow[] = [];
    let rowCount = 0;

    if (dataRows.length) {
      //if (!groups.rows.length)
      //  throw new Error(`No row values provided. Cannot populate pivot.`);

      let rowKeys    = groups.rows.map(x => x.group);
      let columnKeys = groups.columns.map(x => x.group);

      let rowValues    = Array(groups.rows.length);
      let columnValues = Array(groups.columns.length);

      let pivot = this;
      let [topColumn, topRow] = [this.topColumn, this.topRow];

      for (let i = 0; i < dataRows.length; ++i) {
        let sourceRow = dataRows[i];

        let bottomRow = populateRows(sourceRow);
        let bottomColumn = populateColumns(bottomRow, sourceRow);

        for (let { group, dataIndex } of groups.values) {
          let value = pivot.getValue(group, sourceRow[dataIndex]);
          bottomRow.setValue(group, bottomColumn, value);
        }
      }
      
      function populateRows(sourceRow: ReadonlyArray<any>) {
        for (let j = 0; j < groups.rows.length; ++j) {
          let { group, dataIndex } = groups.rows[j];
            if (!group.vf)
              continue;

          rowValues[j] = pivot.getValue(group, sourceRow[dataIndex]);
        }

        let bottomRowResult = topRow.addDescendants(rowKeys, rowValues);
        if (bottomRowResult.added) {
          rowData.push(bottomRowResult.node);
          rowCount++;
        }

        return bottomRowResult.node;
      }

      function populateColumns(bottomRow: PivotRow, sourceRow: ReadonlyArray<any>) {
        if (groups.columns.length) {
          for (let j = 0; j < groups.columns.length; ++j) {
            let { group, dataIndex } = groups.columns[j];
            if (!group.vf)
              continue;

            columnValues[j] = pivot.getValue(group, sourceRow[dataIndex]);
          }

          let bottomColumnResult = topColumn.addDescendants(columnKeys, columnValues);
          let bottomColumn = bottomColumnResult.node;

          return bottomColumnResult.node;
        }
      }
    }

    this.topColumn.sortChildren();

    this.component.ag.ag().api.setPivotResultColumns(
      [...this.topColumn.children()].map(x => x.columnDef)
    );

    return {
      rowData, 
      rowCount 
    };
  }
}

export interface ProcessNewRowsGroup<T extends PivotGroup = PivotGroup> {
  group:     T;
  dataName:  string;
  dataIndex: number;
}

export interface ProcessNewRowsGroups {
  readonly rows:    ReadonlyArray<ProcessNewRowsGroup<PivotRowGroup>>;
  readonly columns: ReadonlyArray<ProcessNewRowsGroup<PivotColumnGroup>>;
  readonly values:  ReadonlyArray<ProcessNewRowsGroup<PivotValueGroup>>;
}

export interface ProcessNewRowsResult {
  readonly rowData:  PivotRow[],
  readonly rowCount: number; 
}
